import type { CSSObject, Theme } from '@emotion/react';

import { forHover } from './responsive';

export const REVEAL_DURATION_MS = 180;

export const motion = {
  hover:
    'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease',
  reveal: `${REVEAL_DURATION_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`,
};

const AUTOFILL_SELECTORS = [
  '&:-webkit-autofill',
  '&:-webkit-autofill:hover',
  '&:-webkit-autofill:focus',
  '&:-webkit-autofill:active',
  '&:autofill',
  '&:autofill:hover',
  '&:autofill:focus',
];

export const autofillSurface = (theme: Theme): CSSObject => {
  const declarations: CSSObject = {
    // Chrome's internal autofill rule sets colour and background !important, so
    // these have to match its importance to win.
    WebkitTextFillColor: `${theme.colors.text.primary} !important`,
    WebkitBoxShadow: `inset 0 0 0 1000px ${theme.colors.background.overlay} !important`,
    caretColor: `${theme.colors.text.primary} !important`,
    // Chrome repaints its own autofill background on a timer; only an
    // absurdly long transition keeps the design-system surface visible.
    transition: 'background-color 8640000s ease-in-out 0s',
  };

  // One rule per selector: a browser that rejects `:autofill` would otherwise
  // drop the whole grouped selector, including the -webkit- fallback.
  return Object.fromEntries(AUTOFILL_SELECTORS.map((selector) => [selector, declarations]));
};

export type HoverEffect =
  | 'surface'
  | 'surfaceStrong'
  | 'surfaceRaised'
  | 'border'
  | 'text'
  | 'brandText'
  | 'underline'
  | 'lift'
  | 'elevate';

export type PressEffect = HoverEffect;

export type FocusRingOptions = {
  target?: string;
  width?: number;
  inset?: boolean;
  within?: boolean;
};

export const DISABLED_EXCLUSIONS =
  ':not(:disabled):not([data-disabled]):not([aria-disabled="true"])';

export const DISABLED_SELECTORS = '&:disabled, &[data-disabled], &[aria-disabled="true"]';

const interactionEffects = (
  theme: Theme,
  phase: 'hover' | 'press',
): Record<HoverEffect, CSSObject> => ({
  surface: { backgroundColor: theme.colors.state.hover },
  surfaceStrong: { backgroundColor: theme.colors.state.hoverStrong },
  surfaceRaised: { backgroundColor: theme.colors.state.hoverRaised },
  border: { borderColor: theme.colors.state.hoverBorder },
  text: { color: theme.colors.state.hoverText },
  brandText: { color: phase === 'hover' ? theme.colors.brand.hover : theme.colors.brand.active },
  underline: { textDecoration: 'underline' },
  lift: { filter: 'brightness(1.25)' },
  elevate: { boxShadow: theme.shadows.md },
});

const mergeEffects = (
  theme: Theme,
  phase: 'hover' | 'press',
  effect: HoverEffect | HoverEffect[],
): CSSObject => {
  const effects = interactionEffects(theme, phase);

  return (Array.isArray(effect) ? effect : [effect]).reduce<CSSObject>(
    (styles, name) => ({ ...styles, ...effects[name] }),
    {},
  );
};

export const hoverable = (
  theme: Theme,
  effect: HoverEffect | HoverEffect[] = 'surface',
  target = '&',
) => ({
  transition: theme.motion.hover,
  [forHover]: {
    [`${target}:hover${DISABLED_EXCLUSIONS}`]: mergeEffects(theme, 'hover', effect),
  },
});

export const pressable = (
  theme: Theme,
  effect: PressEffect | PressEffect[] = 'surface',
  target = '&',
) => ({
  transition: theme.motion.hover,
  [`${target}:active${DISABLED_EXCLUSIONS}`]: mergeEffects(theme, 'press', effect),
});

export const focusRing = (
  theme: Theme,
  { target = '&', width = 2, inset = true, within = false }: FocusRingOptions = {},
) => ({
  [`${target}:${within ? 'focus-within' : 'focus-visible'}${DISABLED_EXCLUSIONS}`]: {
    outline: `${width}px solid ${theme.colors.border.focus}`,
    outlineOffset: inset ? -width : 0,
  },
});

export const CHANGED_FIELD_BORDER_VAR = '--ds-field-border';
export const CHANGED_FIELD_COLOR_VAR = '--ds-changed-color';

export const fieldBorder = (fallback: string) => `var(${CHANGED_FIELD_BORDER_VAR}, ${fallback})`;

export const changedField = (theme: Theme): CSSObject => ({
  '&[data-changed="true"]': {
    [CHANGED_FIELD_BORDER_VAR]: `var(${CHANGED_FIELD_COLOR_VAR}, ${theme.colors.orange.solid})`,
  },
});
